import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Chip,
  TextField,
} from '@mui/material';
import React, { useEffect } from 'react';
import {
  Controller,
  Control,
  FieldValues,
  FieldErrors,
  FieldError,
  UseFormRegister,
} from 'react-hook-form';

export interface TextFieldProps {
  id: string;
  name?: string;
  defaultValue?: string | number;
  required?: boolean;
  control: Control<FieldValues, unknown>;
  errors?: FieldErrors;
  label?: string;
  register?: UseFormRegister<FieldValues>;
  error?: FieldError;
  limitTags?: number;
  readOnly?: boolean;
  disabled?: boolean;
  pattern?: RegExp;
}

export const FreeSoloSelect = ({
  id,
  name,
  defaultValue,
  required = false,
  label,
  control,
  errors,
  error,
  limitTags = 20,
  readOnly = false,
  disabled = false,
  pattern,
}: TextFieldProps) => {
  const [textValue, setInputTextValue] = React.useState('');
  const [showPatternError, setShowPatternError] = React.useState(false);

  useEffect(() => {
    if (pattern && textValue && !pattern.test(textValue)) {
      setShowPatternError(true);
    } else {
      setShowPatternError(false);
    }
  }, [pattern, textValue]);

  const fieldName = name || id;

  const renderTextField = (params: AutocompleteRenderInputParams) => {
    return (
      <TextField
        {...params}
        data-testid="select-text-field"
        fullWidth
        label={label}
        required={required}
        sx={{
          margin: '0',
          padding: '0',
          '&.MuiTextField-root': {
            width: '100%',
            padding: '0',
          },
          '& .MuiAutocomplete-tag': {
            border: '2px solid #C4C4C4',
            background: 'white',
          },
          '.MuiChip-deleteIcon': {
            fontSize: '17px',
          },
          '.MuiAutocomplete-inputRoot': {
            minHeight: '2.75rem',
            width: '100%',
          },
        }}
        variant="standard"
        error={
          (error ? !!error : !!errors && !!errors[id]?.message) ||
          showPatternError
        }
        helperText={
          (error ? error : !!errors && errors[id]?.message) ||
          (showPatternError && 'Invalid Zip Code')
        }
        onKeyDown={(event) => {
          if (pattern && textValue && !pattern.test(textValue)) {
            event.stopPropagation();
          }
        }}
      />
    );
  };

  // controller is built in to react-hook-form and it allows your input to be registered with useForms internal state.
  return (
    <Controller
      control={control}
      name={fieldName}
      defaultValue={defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <Autocomplete
            freeSolo
            readOnly={readOnly}
            disabled={disabled}
            options={[]}
            limitTags={limitTags}
            multiple
            value={value}
            renderInput={(params) => renderTextField(params)}
            onChange={(_, options) => {
              const justValues = options.map((option) => {
                if (typeof option === 'string') {
                  return option;
                }

                return option.value;
              });

              onChange(justValues);
            }}
            renderTags={(value, getTagProps) => {
              return value.map((option, index) => {
                let label = option;

                if (typeof label !== 'string') {
                  label = option.value;
                }

                return <Chip {...getTagProps({ index })} label={label} />;
              });
            }}
            filterOptions={(options, params) => {
              const { inputValue } = params;
              setInputTextValue(inputValue);

              if (!inputValue || (pattern && !pattern.test(inputValue))) {
                return options;
              }

              return [{ label: `Add "${inputValue}"`, value: `${inputValue}` }];
            }}
          />
        );
      }}
    />
  );
};
